import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Download, Printer, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Pager, PageHeader } from "../../components/common/UI";
import { ActiveFilters, FilterBar } from "../../components/reports/FilterBar";
import { Definitions, KpiGrid, Limitations, ReportCharts, ReportTable } from "../../components/reports/ReportBlocks";
import { GROUPS, downloadBlob, useReportMeta } from "../../components/reports/useReportMeta";

const qsToObj = (s) => Object.fromEntries(new URLSearchParams(s));

function Presets({ reportKey, values, onLoad }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/reports/saved", { params: { kind: "preset", report_key: reportKey } }).then((r) => setItems(r.data.items)), [reportKey]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    const name = window.prompt("Name this filter preset");
    if (!name) return;
    try {
      await api.post("/reports/saved", { kind: "preset", name, report_key: reportKey, config: { filters: values } });
      toast.success("Filter preset saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={save} data-testid="preset-save-btn"><Save className="mr-1 h-3.5 w-3.5" />Save preset</Button>
      {items.length > 0 && (
        <select className="otms-input h-8 text-xs" style={{ width: 200 }} defaultValue="" onChange={(e) => { const p = items.find((i) => i.id === e.target.value); if (p) onLoad(p.config.filters || {}); }} data-testid="preset-select">
          <option value="">Load preset…</option>
          {items.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}
    </>
  );
}

export default function ReportView() {
  const { key } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const { can } = useAuth();
  const meta = useReportMeta();
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const values = qsToObj(loc.search);
  const def = meta?.reports.find((r) => r.key === key);

  useEffect(() => {
    if (!meta) return;
    if (!def) { setErr("You do not have access to this report, or it does not exist."); return; }
    setBusy(true); setErr(null);
    api.get(`/reports/run/${key}`, { params: values }).then((r) => setRep(r.data)).catch((e) => setErr(errMsg(e))).finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, loc.search, meta]);

  const apply = (v) => nav(`/reports/${key}?${new URLSearchParams(Object.entries(v).filter(([, x]) => x !== "" && x != null))}`);
  const linkWith = (link, basis) => {
    const [path, q] = link.split("?");
    const drop = ["page", "page_size", "sort", ...(basis === "snapshot" ? ["year", "quarter", "month", "date_from", "date_to"] : [])];
    const keep = Object.fromEntries(Object.entries(values).filter(([k]) => !drop.includes(k)));
    return `${path}?${new URLSearchParams({ ...keep, ...qsToObj(q || "") })}`;
  };
  const exportAs = async (format) => {
    try {
      await downloadBlob(api.get(`/reports/run/${key}/export`, { params: { ...values, format }, responseType: "blob" }), `report.${format}`);
      toast.success(`${format.toUpperCase()} export ready`);
    } catch (e) { toast.error(e?.response?.status === 403 ? "You are not allowed to export this report." : "Export failed"); }
  };

  if (!meta) return <div className="p-8 text-sm text-slate-500" data-testid="report-loading">Loading reports…</div>;
  if (!def) return <div className="rounded-md border border-red-200 bg-red-50 p-6 text-sm text-red-700" data-testid="report-access-denied">{err}</div>;
  const group = Object.values(GROUPS).find((g) => g.includes(key))?.filter((k) => meta.reports.some((r) => r.key === k));
  return (
    <div data-testid={`report-page-${key}`} className="report-print">
      <PageHeader eyebrow="Reports and Analytics" title={def.title} subtitle={def.description}
        actions={
          <div className="flex flex-wrap gap-2 print:hidden">
            {can("measure.export") && ["csv", "xlsx", "pdf"].map((f) => (
              <Button key={f} size="sm" variant="outline" onClick={() => exportAs(f)} data-testid={`export-${f}-btn`}><Download className="mr-1 h-3.5 w-3.5" />{f.toUpperCase()}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="print-btn"><Printer className="mr-1 h-3.5 w-3.5" />Print</Button>
          </div>
        } />
      {group && group.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-1 print:hidden" data-testid="report-group-tabs">
          {group.map((k) => (
            <Link key={k} to={`/reports/${k}${loc.search}`} data-testid={`report-tab-${k}`}
              className={`rounded-full px-3 py-1 text-xs ${k === key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {meta.reports.find((r) => r.key === k).title}
            </Link>
          ))}
        </div>
      )}
      <div className="print:hidden">
        <FilterBar filters={def.filters} values={values} options={meta.options} onApply={apply} onReset={() => nav(`/reports/${key}`)}
          extra={<Presets reportKey={key} values={values} onLoad={apply} />} />
      </div>
      {err && <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="report-error">{err}</div>}
      {busy && <div className="mb-3 text-xs text-slate-400" data-testid="report-busy">Refreshing…</div>}
      {rep && (
        <div className={busy ? "opacity-60 transition-opacity" : "transition-opacity"}>
          <div className="mb-3 text-xs text-slate-500" data-testid="report-period">
            <b>{rep.lgu}</b> · Period {rep.period.label} ({rep.period.timezone}) · Generated {rep.generated_at.replace("T", " ").replace("Z", " UTC")}
          </div>
          <ActiveFilters applied={rep.filters_applied} />
          <KpiGrid kpis={rep.kpis} linkWith={linkWith} />
          <Limitations items={rep.limitations} />
          <ReportCharts charts={rep.charts} />
          {rep.tables.map((t) => <ReportTable key={t.id} t={t} linkWith={linkWith} />)}
          {rep.page && <Pager meta={{ ...rep.page, pages: Math.ceil(rep.page.total / rep.page.page_size) }} onPage={(p) => apply({ ...values, page: p })} />}
          <Definitions rep={rep} />
        </div>
      )}
    </div>
  );
}
